import { useState } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useAuth';
import { resendVerification } from '@/lib/auth-api';

const DISMISS_KEY = 'patronez.verifyBanner.dismissed';

export function UnverifiedEmailBanner() {
  const { data: user } = useCurrentUser();
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1',
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified || dismissed) return null;

  const onResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  const onDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Please confirm your email{user.email ? <> at <strong>{user.email}</strong></> : null} to unlock all features.
          </span>
        </div>
        <div className="flex items-center gap-2">
          {sent ? (
            <span className="text-xs text-amber-900">Sent — check your inbox.</span>
          ) : (
            <button
              onClick={onResend}
              disabled={sending}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-60"
            >
              {sending && <Loader2 className="w-3 h-3 animate-spin" />}
              Resend email
            </button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 rounded-md text-amber-700 hover:bg-amber-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
